import axios from 'axios';

import { Report, ReportCategory, ReportStatus } from '@/common/model/report';
import type { ReporterType, AdminPriority } from '@/common/model/report';
import { BlockedInstance } from '@/common/model/blocked_instance';
import { validateAndEncodeId, handleApiError } from '@/client/service/utils';

/**
 * Filters for querying reports.
 */
export interface ReportFilters {
  status?: ReportStatus;
  category?: ReportCategory;
  eventId?: string;
  source?: ReporterType;
  sortBy?: 'created_at' | 'updated_at' | 'status' | 'category';
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

/**
 * Filters for querying admin reports.
 */
export interface AdminReportFilters {
  page?: number;
  limit?: number;
  status?: string;
  category?: string;
  calendarId?: string;
  source?: string;
  escalationType?: string;
  sortBy?: string;
  sortOrder?: string;
}

/**
 * Data for creating an admin-initiated report.
 */
export interface CreateAdminReportData {
  eventId: string;
  category: string;
  description: string;
  priority: AdminPriority;
  deadline?: string;
  adminNotes?: string;
}

/**
 * Instance-wide moderation settings.
 */
export interface ModerationSettings {
  autoEscalationHours: number;
  adminReportEscalationHours: number;
  reminderBeforeEscalationHours: number;
  autoEscalationThreshold: number;
  ipHashRetentionDays: number;
  ipSubnetRetentionDays: number;
}

/**
 * Pagination metadata returned with paginated report lists.
 */
export interface ReportPagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
}

/**
 * Response shape for paginated report listing.
 */
export interface PaginatedReportsResponse {
  reports: Report[];
  pagination: ReportPagination;
}

/**
 * Escalation history record for a report.
 */
export interface EscalationRecord {
  id: string;
  reportId: string;
  fromStatus: string;
  toStatus: string;
  reviewerId: string | null;
  reviewerRole: string;
  decision: string;
  notes: string | null;
  createdAt: string;
}

/**
 * Response shape for a single report detail with escalation history.
 */
export interface ReportDetailResponse {
  report: Report;
  escalationHistory: EscalationRecord[];
}

/**
 * Error name mapping for moderation API errors.
 */
const errorMap: Record<string, new (...args: any[]) => Error> = {
  ReportNotFoundError: class ReportNotFoundError extends Error {
    constructor() { super('Report not found'); this.name = 'ReportNotFoundError'; }
  },
  ReportAlreadyResolvedError: class ReportAlreadyResolvedError extends Error {
    constructor() { super('Report has already been resolved'); this.name = 'ReportAlreadyResolvedError'; }
  },
  ForbiddenError: class ForbiddenError extends Error {
    constructor() { super('You do not have permission to perform this action'); this.name = 'ForbiddenError'; }
  },
  ValidationError: class ValidationError extends Error {
    constructor() { super('Invalid input'); this.name = 'ValidationError'; }
  },
  EventNotFoundError: class EventNotFoundError extends Error {
    constructor() { super('Event not found'); this.name = 'EventNotFoundError'; }
  },
  DuplicateReportError: class DuplicateReportError extends Error {
    constructor() { super('A report already exists for this event'); this.name = 'DuplicateReportError'; }
  },
  InstanceAlreadyBlockedError: class InstanceAlreadyBlockedError extends Error {
    constructor() { super('Instance is already blocked'); this.name = 'InstanceAlreadyBlockedError'; }
  },
  InvalidDomainError: class InvalidDomainError extends Error {
    constructor() { super('Domain must be a valid domain name'); this.name = 'InvalidDomainError'; }
  },
};

/**
 * Builds a query string from report filters, omitting undefined values.
 *
 * @param filters - The report filter parameters
 * @returns URL query string (including leading '?') or empty string
 */
function buildFilterQuery(filters?: ReportFilters): string {
  if (!filters) {
    return '';
  }

  const params = new URLSearchParams();

  if (filters.page !== undefined) {
    params.set('page', String(filters.page));
  }
  if (filters.limit !== undefined) {
    params.set('limit', String(filters.limit));
  }
  if (filters.status) {
    params.set('status', filters.status);
  }
  if (filters.category) {
    params.set('category', filters.category);
  }
  if (filters.eventId) {
    params.set('eventId', filters.eventId);
  }
  if (filters.source) {
    params.set('source', filters.source);
  }
  if (filters.sortBy) {
    params.set('sortBy', filters.sortBy);
  }
  if (filters.sortOrder) {
    params.set('sortOrder', filters.sortOrder);
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Builds a query string from admin report filters, omitting undefined values.
 *
 * @param filters - The admin report filter parameters
 * @returns URL query string (including leading '?') or empty string
 */
function buildAdminFilterQuery(filters?: AdminReportFilters): string {
  if (!filters) {
    return '';
  }

  const params = new URLSearchParams();

  if (filters.page !== undefined) {
    params.set('page', String(filters.page));
  }
  if (filters.limit !== undefined) {
    params.set('limit', String(filters.limit));
  }
  if (filters.status) {
    params.set('status', filters.status);
  }
  if (filters.category) {
    params.set('category', filters.category);
  }
  if (filters.calendarId) {
    params.set('calendar_id', filters.calendarId);
  }
  if (filters.source) {
    params.set('source', filters.source);
  }
  if (filters.escalationType) {
    params.set('escalationType', filters.escalationType);
  }
  if (filters.sortBy) {
    params.set('sortBy', filters.sortBy);
  }
  if (filters.sortOrder) {
    params.set('sortOrder', filters.sortOrder);
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Client service for calendar owner moderation endpoints.
 *
 * Provides methods for listing, viewing, and acting on reports
 * filed against events in a calendar. Also provides admin-level
 * methods for instance-wide report management and moderation settings.
 */
export default class ModerationService {

  /**
   * Fetches a paginated list of reports for a calendar with optional filters.
   *
   * @param calendarId - The calendar UUID
   * @param filters - Optional filtering, sorting, and pagination parameters
   * @returns Paginated list of reports
   */
  async getReports(calendarId: string, filters?: ReportFilters): Promise<PaginatedReportsResponse> {
    try {
      const encodedCalendarId = validateAndEncodeId(calendarId, 'Calendar ID');
      const query = buildFilterQuery(filters);
      const response = await axios.get(`/api/v1/calendars/${encodedCalendarId}/reports${query}`);

      return {
        reports: response.data.reports.map((reportData: Record<string, any>) => Report.fromObject(reportData)),
        pagination: response.data.pagination,
      };
    }
    catch (error: unknown) {
      console.error('Error fetching reports:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Fetches a single report detail with its escalation history.
   *
   * @param calendarId - The calendar UUID
   * @param reportId - The report UUID
   * @returns Report detail with escalation history
   */
  async getReport(calendarId: string, reportId: string): Promise<ReportDetailResponse> {
    try {
      const encodedCalendarId = validateAndEncodeId(calendarId, 'Calendar ID');
      const encodedReportId = validateAndEncodeId(reportId, 'Report ID');
      const response = await axios.get(
        `/api/v1/calendars/${encodedCalendarId}/reports/${encodedReportId}`,
      );

      return {
        report: Report.fromObject(response.data.report),
        escalationHistory: response.data.escalationHistory ?? [],
      };
    }
    catch (error: unknown) {
      console.error('Error fetching report detail:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Updates the owner notes on a report.
   *
   * @param calendarId - The calendar UUID
   * @param reportId - The report UUID
   * @param ownerNotes - The notes text to set
   * @returns The updated report
   */
  async updateReportNotes(calendarId: string, reportId: string, ownerNotes: string): Promise<Report> {
    try {
      const encodedCalendarId = validateAndEncodeId(calendarId, 'Calendar ID');
      const encodedReportId = validateAndEncodeId(reportId, 'Report ID');
      const response = await axios.put(
        `/api/v1/calendars/${encodedCalendarId}/reports/${encodedReportId}`,
        { ownerNotes },
      );

      return Report.fromObject(response.data.report);
    }
    catch (error: unknown) {
      console.error('Error updating report notes:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Resolves a report with required notes explaining the resolution.
   *
   * @param calendarId - The calendar UUID
   * @param reportId - The report UUID
   * @param notes - Resolution notes (required)
   * @returns The resolved report
   */
  async resolveReport(calendarId: string, reportId: string, notes: string): Promise<Report> {
    try {
      const encodedCalendarId = validateAndEncodeId(calendarId, 'Calendar ID');
      const encodedReportId = validateAndEncodeId(reportId, 'Report ID');
      const response = await axios.post(
        `/api/v1/calendars/${encodedCalendarId}/reports/${encodedReportId}/resolve`,
        { notes },
      );

      return Report.fromObject(response.data.report);
    }
    catch (error: unknown) {
      console.error('Error resolving report:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Dismisses a report. This automatically escalates the report to admin review.
   *
   * @param calendarId - The calendar UUID
   * @param reportId - The report UUID
   * @param notes - Dismissal notes (required)
   * @returns The dismissed/escalated report
   */
  async dismissReport(calendarId: string, reportId: string, notes: string): Promise<Report> {
    try {
      const encodedCalendarId = validateAndEncodeId(calendarId, 'Calendar ID');
      const encodedReportId = validateAndEncodeId(reportId, 'Report ID');
      const response = await axios.post(
        `/api/v1/calendars/${encodedCalendarId}/reports/${encodedReportId}/dismiss`,
        { notes },
      );

      return Report.fromObject(response.data.report);
    }
    catch (error: unknown) {
      console.error('Error dismissing report:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Forwards a report to the remote calendar owner via ActivityPub.
   * Only valid for reposted events with eventSourceUrl.
   *
   * @param calendarId - The calendar UUID
   * @param reportId - The report UUID
   * @param message - Optional message to include with the report
   * @returns The updated report with forwardStatus set to 'pending'
   */
  async forwardReport(calendarId: string, reportId: string, message?: string): Promise<Report> {
    try {
      const encodedCalendarId = validateAndEncodeId(calendarId, 'Calendar ID');
      const encodedReportId = validateAndEncodeId(reportId, 'Report ID');
      const response = await axios.post(
        `/api/v1/calendars/${encodedCalendarId}/reports/${encodedReportId}/forward`,
        { message: message || '' },
      );

      return Report.fromObject(response.data.report);
    }
    catch (error: unknown) {
      console.error('Error forwarding report:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Updates an editor's moderation permission for a calendar.
   *
   * @param calendarId - The calendar UUID
   * @param editorId - The editor UUID
   * @param canReviewReports - Whether the editor can review reports
   */
  async updateEditorPermissions(calendarId: string, editorId: string, canReviewReports: boolean): Promise<void> {
    try {
      const encodedCalendarId = validateAndEncodeId(calendarId, 'Calendar ID');
      const encodedEditorId = validateAndEncodeId(editorId, 'Editor ID');
      await axios.put(
        `/api/v1/calendars/${encodedCalendarId}/editors/${encodedEditorId}/permissions`,
        { canReviewReports },
      );
    }
    catch (error: unknown) {
      console.error('Error updating editor permissions:', error);
      handleApiError(error, errorMap);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Admin report methods
  // ──────────────────────────────────────────────────────────────

  /**
   * Fetches a paginated list of admin-relevant reports with optional filters.
   * Returns escalated reports and admin-initiated reports.
   *
   * @param filters - Optional filtering, sorting, and pagination parameters
   * @returns Paginated list of reports
   */
  async getAdminReports(filters?: AdminReportFilters): Promise<PaginatedReportsResponse> {
    try {
      const query = buildAdminFilterQuery(filters);
      const response = await axios.get(`/api/v1/admin/reports${query}`);

      return {
        reports: response.data.reports.map((reportData: Record<string, any>) => Report.fromObject(reportData)),
        pagination: response.data.pagination,
      };
    }
    catch (error: unknown) {
      console.error('Error fetching admin reports:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Fetches a single admin report detail with escalation history.
   *
   * @param reportId - The report UUID
   * @returns Report detail with escalation history
   */
  async getAdminReport(reportId: string): Promise<ReportDetailResponse> {
    try {
      const encodedReportId = validateAndEncodeId(reportId, 'Report ID');
      const response = await axios.get(`/api/v1/admin/reports/${encodedReportId}`);

      return {
        report: Report.fromObject(response.data.report),
        escalationHistory: response.data.escalationHistory ?? [],
      };
    }
    catch (error: unknown) {
      console.error('Error fetching admin report detail:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Resolves a report as an admin with notes.
   *
   * @param reportId - The report UUID
   * @param notes - Resolution notes (required)
   * @returns The resolved report
   */
  async adminResolveReport(reportId: string, notes: string): Promise<Report> {
    try {
      const encodedReportId = validateAndEncodeId(reportId, 'Report ID');
      const response = await axios.put(
        `/api/v1/admin/reports/${encodedReportId}`,
        { action: 'resolve', notes },
      );

      return Report.fromObject(response.data.report);
    }
    catch (error: unknown) {
      console.error('Error resolving admin report:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Dismisses a report as an admin with notes.
   *
   * @param reportId - The report UUID
   * @param notes - Dismissal notes (required)
   * @returns The dismissed report
   */
  async adminDismissReport(reportId: string, notes: string): Promise<Report> {
    try {
      const encodedReportId = validateAndEncodeId(reportId, 'Report ID');
      const response = await axios.put(
        `/api/v1/admin/reports/${encodedReportId}`,
        { action: 'dismiss', notes },
      );

      return Report.fromObject(response.data.report);
    }
    catch (error: unknown) {
      console.error('Error dismissing admin report:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Overrides a calendar owner's decision on a report.
   *
   * @param reportId - The report UUID
   * @param notes - Override notes (required)
   * @returns The overridden report
   */
  async adminOverrideReport(reportId: string, notes: string): Promise<Report> {
    try {
      const encodedReportId = validateAndEncodeId(reportId, 'Report ID');
      const response = await axios.put(
        `/api/v1/admin/reports/${encodedReportId}`,
        { action: 'override', notes },
      );

      return Report.fromObject(response.data.report);
    }
    catch (error: unknown) {
      console.error('Error overriding admin report:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Creates an admin-initiated report for an event.
   * Admin reports skip email verification and go directly to submitted status.
   *
   * @param data - The report creation data
   * @returns The newly created report
   */
  async createAdminReport(data: CreateAdminReportData): Promise<Report> {
    try {
      const response = await axios.post('/api/v1/admin/reports', data);

      return Report.fromObject(response.data.report);
    }
    catch (error: unknown) {
      console.error('Error creating admin report:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Forwards a report to the owner of the calendar the event came from
   * (DEC-015 — the method name says "remote admin" but the recipient is the
   * origin calendar's actor). Only applicable for reports on remote events.
   *
   * @param reportId - The report UUID
   */
  async adminForwardToRemoteAdmin(reportId: string): Promise<void> {
    try {
      const encodedReportId = validateAndEncodeId(reportId, 'Report ID');
      await axios.post(`/api/v1/admin/reports/${encodedReportId}/forward-to-admin`);
    }
    catch (error: unknown) {
      console.error('Error forwarding report to remote admin:', error);
      handleApiError(error, errorMap);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Moderation settings methods
  // ──────────────────────────────────────────────────────────────

  /**
   * Fetches instance-wide moderation settings.
   *
   * @returns Current moderation settings
   */
  async getModerationSettings(): Promise<ModerationSettings> {
    try {
      const response = await axios.get('/api/v1/admin/moderation/settings');
      return response.data;
    }
    catch (error: unknown) {
      console.error('Error fetching moderation settings:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Updates instance-wide moderation settings. Supports partial updates.
   *
   * @param settings - Partial settings to update
   * @returns Updated moderation settings
   */
  async updateModerationSettings(settings: Partial<ModerationSettings>): Promise<ModerationSettings> {
    try {
      const response = await axios.put('/api/v1/admin/moderation/settings', settings);
      return response.data;
    }
    catch (error: unknown) {
      console.error('Error updating moderation settings:', error);
      handleApiError(error, errorMap);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Instance blocking methods
  // ──────────────────────────────────────────────────────────────

  /**
   * Fetches list of all blocked instances.
   *
   * @returns Array of blocked instance domain models
   */
  async listBlockedInstances(): Promise<BlockedInstance[]> {
    try {
      const response = await axios.get('/api/v1/admin/moderation/blocked-instances');
      return response.data.map((data: Record<string, any>) => BlockedInstance.fromObject(data));
    }
    catch (error: unknown) {
      console.error('Error fetching blocked instances:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Blocks an instance from federating with this instance.
   *
   * @param domain - The domain to block (e.g., 'bad-instance.example.com')
   * @param reason - Reason for blocking the instance
   * @returns The newly created BlockedInstance
   */
  async blockInstance(domain: string, reason: string): Promise<BlockedInstance> {
    try {
      const response = await axios.post('/api/v1/admin/moderation/block-instance', {
        domain,
        reason,
      });
      return BlockedInstance.fromObject(response.data);
    }
    catch (error: unknown) {
      console.error('Error blocking instance:', error);
      handleApiError(error, errorMap);
    }
  }

  /**
   * Unblocks an instance, allowing it to federate again.
   *
   * @param domain - The domain to unblock
   */
  async unblockInstance(domain: string): Promise<void> {
    try {
      await axios.delete(`/api/v1/admin/moderation/blocked-instances/${encodeURIComponent(domain)}`);
    }
    catch (error: unknown) {
      console.error('Error unblocking instance:', error);
      handleApiError(error, errorMap);
    }
  }
}
